import React, { useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { ScrollToOptions, useVirtualizer } from '@tanstack/react-virtual'
import { useEffectOnce } from 'react-use'
import { type ChatId, useSelectedChatKey } from '../../blocs/chatVisibility'
import { ChatMessage } from '../ChatMessage/ChatMessage'
import {
  getNextPage,
  isFetchingNextPage,
  useChatMessages,
  useHasNextPage,
} from '../../blocs/useChat'
import { usePinScrollToBottom } from './usePinScrollToBottom'

import './ChatMessages.scss'

interface ChatMessagesProps {
  type: ChatId
}

const ChatMessages = ({ type }: ChatMessagesProps) => {
  const chatMessageIds = useChatMessages(type)
  const canFetchNextPage = useHasNextPage(type)
  const scrollContainer = useRef<HTMLDivElement>(null)

  const [loadedFirstPage, setLoadedFirstPage] = useState(false)

  const virtualizer = useVirtualizer({
    count: chatMessageIds?.length || 0,
    getScrollElement: () => scrollContainer.current,
    estimateSize: () => 65,
    overscan: 5,
  })

  const items = virtualizer.getVirtualItems()

  useEffectOnce(() => {
    virtualizer.measure()
  })

  const scrollToIndex = useCallback(
    (index: number, args: ScrollToOptions) => {
      virtualizer.scrollToIndex(index, args)
      if (!loadedFirstPage) {
        setTimeout(() => {
          setLoadedFirstPage(true)
        }, 100)
      }
    },
    [loadedFirstPage, virtualizer]
  )

  usePinScrollToBottom(
    scrollContainer,
    virtualizer,
    scrollToIndex,
    chatMessageIds?.length
  )

  useEffect(() => {
    if (!chatMessageIds) return
    if (!canFetchNextPage || isFetchingNextPage(type) || !loadedFirstPage)
      return

    const [firstItem] = items
    const [lastItem] = [...items].reverse()

    if (!lastItem) return

    if (!chatMessageIds[lastItem.index] || !chatMessageIds[firstItem.index])
      getNextPage(type)
  }, [canFetchNextPage, chatMessageIds, loadedFirstPage, type, items])

  const isHidden = useSelectedChatKey() !== type

  if (chatMessageIds?.length === 0) {
    return (
      <div className="webinar-chat-no-messages" data-hidden={isHidden}>
        <FormattedMessage defaultMessage="No chat messages have been posted" />
      </div>
    )
  }

  return (
    <div
      ref={scrollContainer}
      className="webinar-chat-messages"
      data-hidden={isHidden}
    >
      {chatMessageIds && (
        <div
          className="webinar-chat-messages-list-wrapper"
          style={{
            height: virtualizer.getTotalSize(),
          }}
        >
          {items.map((virtualItem) => {
            const commentId = chatMessageIds[virtualItem.index]
            return (
              <div
                className="webinar-chat-message-wrapper"
                key={`${commentId}-${virtualItem.index}`}
                ref={virtualizer.measureElement}
                data-index={virtualItem.index}
                style={{
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                <ChatMessage
                  key={commentId}
                  hash={virtualItem.index}
                  id={commentId}
                />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
export { ChatMessages }
